// React imports
import React, { useState, useEffect } from 'react';

// MUI imports
import { Modal, Box, Grid, TextField, Autocomplete, CircularProgress, IconButton, Tooltip, } from '@mui/material';

// MUI Icons imports
import { Add as AddIcon, Close as CloseIcon } from '@mui/icons-material';

// Customized MUI components
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import AddContactModal from 'components/AddContactModal';

// Other imports
import { useAuth } from '../../auth/Auth';
import { useTravelUi } from '../../hooks/useTravelUi';
import { useNavigate } from 'react-router-dom';
import colors from "assets/theme/base/colors";


const AddCaseModal = ({
    isOpen,
    onClose,
    onSave,
    preselectedAccount
}) => {
    const { axiosInstance, user } = useAuth();
    const [formData, setFormData] = useState({
        account_id: '',
        account_name: '',
        status: 'Draft',
        priority: 'Low',
        internal_status: '',
        category: '',
        sub_category: '',
        subject: '',
        contact_id: '',
        contact_name: '',
        order_id: null, // Changed to null
        order_number: '',
        description: '',
        case_source: 'Email',
        case_reason: '',
        email: '',
        travel_order_id: null,
        order_pax_id: null,
        customer_id: null,
        merchant_id: null,
        coupon_id: null,
        case_number: '',
        assigned_to: user?.id || '',
        assigned_to_name: user?.username || '',
    });
    const [accounts, setAccounts] = useState([]);
    const [contacts, setContacts] = useState([]);
    const [orders, setOrders] = useState([]);
    const [travelOrders, setTravelOrders] = useState([]);
    const [orderPax, setOrderPax] = useState([]);
    const [merchants, setMerchants] = useState([]);
    const [coupons, setCoupons] = useState([]);
    const [loadingOrders, setLoadingOrders] = useState(false);
    const [loadingAccounts, setLoadingAccounts] = useState(false);
    const [loadingContacts, setLoadingContacts] = useState(false);
    const [loadingTravelOrders, setLoadingTravelOrders] = useState(false);
    const [loadingOrderPax, setLoadingOrderPax] = useState(false);
    const [loadingMerchants, setLoadingMerchants] = useState(false);
    const [loadingCoupons, setLoadingCoupons] = useState(false);
    const [formErrors, setFormErrors] = useState({});
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [users, setUsers] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [newContactModalOpen, setNewContactModalOpen] = useState(false);
    const [newContact, setNewContact] = useState(null);
    const [isContactModalOpen, setIsContactModalOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    // Travel fields need the TRAVEL edition and the company travel toggle.
    const { showTravel: isTravelEnabled } = useTravelUi();
    const navigate = useNavigate();

    // Reset dependent fields when modal opens/closes
    useEffect(() => {
        if (!isOpen) {
            setFormErrors({});
            setContacts([]);
            setOrders([]);
            setTravelOrders([]);
            setSelectedAccount(null);
            setFormData(prev => ({
                ...prev,
                contact_id: '',
                contact_name: '',
                order_id: '',
                order_number: '',
            }));
        }
    }, [isOpen]);

    // Check whether travel module is enabled or not.
    // Retrieve distinct options for all customizable options fields.

    useEffect(() => {
        fetchRelatedOptions();
    }, []);

    // Update the options whenever the travel switch changes.

    useEffect(() => {
        fetchRelatedOptions();
    }, [isTravelEnabled])


    // Merge default options + fetched options, de-duped, defaults first
    const mergeOptions = (defaults, fetched) => {
        if (!fetched?.length) return defaults;
        const seen = new Set(defaults.map((v) => v.toLowerCase()));
        const extras = fetched.filter((v) => v && !seen.has(v.toLowerCase()));
        return [...defaults, ...extras];
    };

    const mergeSubCategoryOptions = (defaults, fetched) => {
        if (!fetched || typeof fetched !== 'object' || !Object.keys(fetched).length) {
            return defaults;
        }
        const merged = { ...defaults };
        for (const [category, subCats] of Object.entries(fetched)) {
            const existing = merged[category] || [];
            const seen = new Set(existing.map((v) => v.toLowerCase()));
            const extras = subCats.filter((v) => v && !seen.has(v.toLowerCase()));
            merged[category] = [...existing, ...extras];
        }
        return merged;
    };

    // Default options for fields.
    const DEFAULT_INTERNAL_STATUS_OPTIONS = [
        "Pending Internal Team", "Pending Third Party", "Resolution in Progress", "Ready for Closure",
    ];
    const DEFAULT_CATEGORY_OPTIONS = [
        "Support", "Complaint", "Technical Issue", "Billing Issue", "Product Query", "Service Request", "General Query"
    ];
    const DEFAULT_TRAVEL_CATEGORY_OPTIONS = [
        "Billing & Payment", "Booking Issue", "Cancellation & Refund", "Technical Issue", "Complaint", "Service Request",
        "Trip Operations & Support", "Travel Documentation", "General Query"
    ];
    const DEFAULT_SUB_CATEGORY_OPTIONS = {
        "Support": ["Account Help", "Order Help"],
        "Complaint": ["Service Quality", "Incorrect Information"],
        "Technical Issue": ["Login & Access Issue", "App/System Issue"],
        "Billing Issue": ["Payment Issue", "Invoice Issue"],
        "Product Query": ["Product Information", "Feature Query"],
        "Service Request": ["Callback Request", "Information Update"],
        "General Query": ["General Information", "Other"],
    };
    const DEFAULT_TRAVEL_SUB_CATEGORY_OPTIONS = {
        "Billing & Payment": ["Payment Issue", "Invoice Issue", "Billing Clarification"],
        "Booking Issue": ["Booking Confirmation", "Passenger Details", "Booking Modification"],
        "Cancellation & Refund": ["Cancellation Issue", "Refund Issue", "Refund Status Query"],
        "Technical Issue": ["Login & Access Issue", "App/System Issue"],
        "Complaint": ["Service Quality", "Incorrect Information"],
        "Service Request": ["Document Request", "Information Update"],
        "Trip Operations & Support": ["Flight Support", "Accommodation Support", "Transfer Support", "Activity Support", "On-Trip Assistance", "Emergency Assistance"],
        "Travel Documentation": ["Visa Support", "Passport & Ticket Support"],
        "General Query": ["General Information", "Other"],
    }
    const DEFAULT_CASE_SOURCE_OPTIONS = ["Website Support Form", "Email Support", "Internal CRM"];
    const DEFAULT_STATUS_OPTIONS = [
        "New", "In Progress", "On Hold", "Awaiting Customer Information", "Closed", "Rejected", "Reopened", "Draft",
    ];

    // State management for options
    const [internalStatusOptions, setInternalStatusOptions] = useState(DEFAULT_INTERNAL_STATUS_OPTIONS);
    const [categoryOptions, setCategoryOptions] = useState(isTravelEnabled ? DEFAULT_TRAVEL_CATEGORY_OPTIONS : DEFAULT_CATEGORY_OPTIONS);
    const [subCategoryOptions, setSubCategoryOptions] = useState(isTravelEnabled ? DEFAULT_TRAVEL_SUB_CATEGORY_OPTIONS : DEFAULT_SUB_CATEGORY_OPTIONS);
    const [caseSourceOptions, setCaseSourceOptions] = useState(DEFAULT_CASE_SOURCE_OPTIONS);
    const [statusOptions, setStatusOptions] = useState(DEFAULT_STATUS_OPTIONS);

    // GET customizable options.

    const fetchRelatedOptions = async () => {
        try {
            const response = await axiosInstance.get('/cases/configurable-options');
            setInternalStatusOptions(
                mergeOptions(DEFAULT_INTERNAL_STATUS_OPTIONS, response.data?.internal_statuses)
            );
            setCategoryOptions(
                mergeOptions(isTravelEnabled ? DEFAULT_TRAVEL_CATEGORY_OPTIONS: DEFAULT_CATEGORY_OPTIONS, response.data?.categories)
            );
            setCaseSourceOptions(
                mergeOptions(DEFAULT_CASE_SOURCE_OPTIONS, response.data?.case_sources)
            );
            setStatusOptions(
                mergeOptions(DEFAULT_STATUS_OPTIONS, response.data?.statuses)
            );
            setSubCategoryOptions(
                mergeSubCategoryOptions(isTravelEnabled ? DEFAULT_TRAVEL_SUB_CATEGORY_OPTIONS : DEFAULT_SUB_CATEGORY_OPTIONS, response.data?.sub_categories)
            );

        } catch (error) {
            console.error('Error fetching case options:', error);
        }
    };

    // Fetch initial data when modal opens
    useEffect(() => {
        if (isOpen) {

            // GET all accounts.
            setLoadingAccounts(true);
            axiosInstance.get('/accounts/',
                {
                    params: {
                        account_type: isTravelEnabled ? 'partner' : null,
                    },
                })
                .then(response => {
                    setAccounts(response.data);
                })
                .catch(error => {
                    console.error('Error fetching accounts:', error);
                })
                .finally(() => {
                    setLoadingAccounts(false);
                });

            // GET users.
            setLoadingUsers(true);
            axiosInstance.get('/users/')
            .then(response => {
                setUsers(response.data);
            })
            .catch(error => {
                console.error('Error fetching users:', error);
            })
            .finally(() => {
                setLoadingUsers(false);
            });

            // GET travel orders.
            if (isTravelEnabled) {
                setLoadingTravelOrders(true);
                axiosInstance.get('/travel-orders/')
                .then(response => setTravelOrders(response.data || []))
                .catch(error => console.error('Error fetching travel orders:', error))
                .finally(() => setLoadingTravelOrders(false));
            }

            // setLoadingMerchants(true);
            // axiosInstance.get('/merchants/')
            //     .then(response => setMerchants(response.data || []))
            //     .catch(error => console.error('Error fetching merchants:', error))
            //     .finally(() => setLoadingMerchants(false));

            // setLoadingCoupons(true);
            // axiosInstance.get('/coupons/')
            //     .then(response => setCoupons(response.data || []))
            //     .catch(error => console.error('Error fetching coupons:', error))
            //     .finally(() => setLoadingCoupons(false));
        }
    }, [isOpen, axiosInstance, isTravelEnabled]);

    // Fetch order pax when travel order is selected
    useEffect(() => {
        if (formData.travel_order_id) {
            setLoadingOrderPax(true);
            axiosInstance.get(`/travel-order-pax/?travel_order_id=${formData.travel_order_id}`)
                .then(response => setOrderPax(response.data || []))
                .catch(error => console.error('Error fetching order pax:', error))
                .finally(() => setLoadingOrderPax(false));
        } else {
            setOrderPax([]);
        }
    }, [formData.travel_order_id, axiosInstance]);

    // Fetch contacts and orders when account is selected
    useEffect(() => {
        if (selectedAccount?.id) {
            // Fetch contacts and orders
            setLoadingContacts(true);
            axiosInstance.get(`/contacts/?account_id=${selectedAccount.id}`)
                .then(response => setContacts(response.data))
                .catch(error => console.error('Error fetching contacts:', error))
                .finally(() => setLoadingContacts(false));

            setLoadingOrders(true);
            axiosInstance.get(`/orders/account/${selectedAccount.id}`)
                .then(response => setOrders(response.data))
                .catch(error => console.error('Error fetching orders:', error))
                .finally(() => setLoadingOrders(false));
        } else {
            setContacts([]);
            setOrders([]);
        }
    }, [selectedAccount, axiosInstance]);

    useEffect(() => {
        if (preselectedAccount && isOpen) {
            setSelectedAccount(preselectedAccount);
            setFormData(prev => ({
                ...prev,
                account_id: preselectedAccount.id,
                account_name: preselectedAccount.name,
            }));

            // Fetch contacts for the preselected account
            fetchContacts(preselectedAccount.id);
        }
    }, [preselectedAccount, isOpen]);

    const fetchContacts = (accountId) => {
        setLoadingContacts(true);
        axiosInstance.get(`/contacts/?account_id=${accountId}`)
            .then(response => {
                const contactsData = response.data;
                setContacts(contactsData);

                // Check for primary contact and set it in formData
                const primaryContact = contactsData.find(contact => contact.primary_contact);
                if (primaryContact) {
                    setFormData(prev => ({
                        ...prev,
                        contact_id: primaryContact.id,
                        contact_name: primaryContact.first_name
                            ? `${primaryContact.first_name} ${primaryContact.last_name}`
                            : primaryContact.last_name || '', // Fallback to last name only
                    }));
                }
            })
            .catch(error => console.error('Error fetching contacts:', error))
            .finally(() => setLoadingContacts(false));
    };

    const handleAccountChange = (_, newValue) => {
        setSelectedAccount(newValue);
        setFormData(prev => ({
            ...prev,
            account_id: newValue?.id || '',
            account_name: newValue?.name || '',
        }));
    };

    const handleContactChange = (_, newValue) => {
        setFormData(prev => ({
            ...prev,
            contact_id: newValue?.id || '',
            contact_name: newValue
                ? newValue.first_name
                    ? `${newValue.first_name} ${newValue.last_name}`
                    : newValue.last_name
                : '',
        }));
    };


    const handleOrderChange = (_, newValue) => {
        setFormData(prev => ({
            ...prev,
            order_id: newValue?.id || '', // Set to null if no order is selected
            order_number: newValue?.order_number || '',
        }));
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        setFormErrors(prev => ({ ...prev, [name]: '' }));
    };
    const handleOpenContactModal = () => {
        setIsContactModalOpen(true);
    };

    const handleCloseContactModal = () => {
        setIsContactModalOpen(false);
    };

    // Validate required fields.

    const validateForm = () => {
        const errors = {};

        if (!isTravelEnabled) {
            // if (!formData.subject) errors.subject = 'Subject is required';
            if (!formData.contact_id) errors.contact_id = 'Contact is required';
            if (!formData.account_id) errors.account_id = 'Account is required';
        } else {
            if (!formData.email) errors.email = 'Email is required';
            const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (formData.email && !emailPattern.test(formData.email.trim())) {
                errors.email = 'Invalid Email address';
            }
        }

        if (!formData.status) errors.status = 'Status is required';
        if (!formData.case_source) errors.case_source = 'Case Source is required';
        if (!formData.category) errors.category = 'Category is required';
        if (!formData.subject) errors.subject = 'Subject is required';
        if (!formData.description) errors.description = 'Description is required';

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // Handle Input Fields update.

    const handleChange = (key, value) => {
        setFormData((prev) => ({
            ...prev,
            [key]: value,
        }));

        setFormErrors((prev) => {
            if (!prev[key]) return prev; // no error for this field, nothing to change
            const updated = { ...prev };
            delete updated[key];
            return updated;
        });
    };


    const handleSave = () => {
        if (validateForm()) {
            onSave(formData);
            onClose();
        }
    };

    const handleNewContactSave = (contact) => {
        setNewContact(contact);
        setFormData(prev => ({
            ...prev,
            contact_id: contact.id,
            contact_name: contact.last_name,
        }));
        setNewContactModalOpen(false);

        // Fetch updated contacts list
        fetchContacts(selectedAccount.id);
    };

    const handleSaveCase = async (e) => {
        e.preventDefault();
        if (validateForm()) {
            setIsSaving(true);
            try {
                // Get the next case number
                const casesResponse = await axiosInstance.get('/cases/');
                const nextCaseNumber = casesResponse.data.length + 1;
                const uniqueCaseNumber = `CASE-${String(nextCaseNumber).padStart(5, '0')}`;

                // Prepare the payload with proper data types
                const payload = {
                    ...formData,
                    case_number: uniqueCaseNumber,
                    created_by: Number(user?.id),
                    updated_by: Number(user?.id),
                    created_by_name: user?.username,
                    updated_by_name: user?.username,
                    created_at: new Date().toISOString(),
                    updated_at: new Date().toISOString(),
                    account_id: formData.account_id ? Number(formData.account_id) : null,
                    contact_id: formData.contact_id ? Number(formData.contact_id) : null,
                    order_id: formData.order_id ? Number(formData.order_id) : null,
                    assigned_to: Number(formData.assigned_to),
                    travel_order_id: formData.travel_order_id ? Number(formData.travel_order_id) : null,
                    order_pax_id: formData.order_pax_id ? Number(formData.order_pax_id) : null,
                    customer_id: formData.customer_id ? Number(formData.customer_id) : null,
                    merchant_id: formData.merchant_id ? Number(formData.merchant_id) : null,
                    coupon_id: formData.coupon_id ? Number(formData.coupon_id) : null
                };

                // Save the case and navigate to details
                const newCaseResponse = await axiosInstance.post('/cases/', payload);
                navigate(`/cases/${newCaseResponse.data.id}`);
            } catch (error) {
                console.error('Error saving case:', error);

                // Handle specific validation errors
                if (error.response?.status === 422) {
                    const validationErrors = {};
                    error.response.data.detail?.forEach(error => {
                        validationErrors[error.loc[1]] = error.msg;
                    });
                    setFormErrors(validationErrors);
                }
            } finally {
                setIsSaving(false);
            }
        }
    };


    return (
        <Modal open={isOpen} onClose={onClose} aria-labelledby="case-form-modal-title">
            <Box sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: { xs: '90%', sm: '80%', md: '70%', lg: '60%' },
                maxWidth: 800,
                maxHeight: '90vh',
                bgcolor: 'background.paper',
                boxShadow: 24,
                borderRadius: 2,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
            }}>
                {/* Header */}
                <Box className="pearl-popup-header"
                    sx={{
                        backgroundColor: colors.popupHeader.background,
                        px: 4,
                        py: 3,
                        position: 'sticky',
                        top: 0,
                        zIndex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <MDTypography 
                        id="case-form-modal-title" 
                        variant="h6" 
                        component="h2"
                        sx={{ color: '#FFFFFF' }}
                    >
                        Add New Case
                    </MDTypography>
                    <IconButton
                        aria-label="Close"
                        onClick={onClose}
                        disabled={isSaving}
                        sx={{ color: '#FFFFFF', ml: 1 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>

                {/* Scrollable content area */}
                <Box 
                    sx={{ 
                        overflowY: 'auto',
                        p: 4,
                        '&::-webkit-scrollbar': {
                            width: '0.4em'
                        },
                        '&::-webkit-scrollbar-track': {
                            background: '#f1f1f1',
                            borderRadius: '4px'
                        },
                        '&::-webkit-scrollbar-thumb': {
                            backgroundColor: '#888',
                            borderRadius: '4px'
                        },
                        '&::-webkit-scrollbar-thumb:hover': {
                            background: '#555'
                        }
                    }}
                >
                    <form onSubmit={handleSaveCase}>

                        <Grid container spacing={2}>

                            {/* Account Field */}

                            <Grid item xs={12} sm={6}>
                                <Autocomplete
                                    options={accounts}
                                    getOptionLabel={(option) => option.name}
                                    value={selectedAccount}
                                    onChange={handleAccountChange}
                                    defaultValue={preselectedAccount} // Add this line
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Account"
                                            variant="outlined"
                                            margin="normal"
                                            fullWidth
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <>
                                                        {loadingAccounts && <CircularProgress color="inherit" size={20} />}
                                                        {params.InputProps.endAdornment}
                                                    </>
                                                ),
                                            }}
                                            error={!!formErrors.account_id}
                                            helperText={formErrors.account_id}
                                            sx={{ height: '40px' }}
                                        />
                                    )}
                                />
                            </Grid>

                            {/* Account Contact */}

                            <Grid item xs={12} sm={6}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box sx={{ flex: 1 }}>
                                        <Autocomplete
                                            options={contacts}
                                            getOptionLabel={(option) =>
                                                option.first_name
                                                    ? `${option.first_name} ${option.last_name}`
                                                    : option.last_name
                                            }
                                            onChange={handleContactChange}
                                            disabled={!selectedAccount}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Contact"
                                                    variant="outlined"
                                                    margin="normal"
                                                    fullWidth
                                                    error={!!formErrors.contact_id}
                                                    helperText={formErrors.contact_id}
                                                    InputProps={{
                                                        ...params.InputProps,
                                                        endAdornment: (
                                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                                {loadingContacts && <CircularProgress color="inherit" size={20} />}
                                                                {params.InputProps.endAdornment}
                                                                {formData.contact_id === '' && (
                                                                    <Tooltip title="Add New Contact" placement="top">
                                                                        <IconButton
                                                                            onClick={handleOpenContactModal}
                                                                            disabled={!selectedAccount}
                                                                            size="small"
                                                                            color="primary"
                                                                            sx={{
                                                                                ml: 1,
                                                                                backgroundColor: '#F7F7F7',
                                                                                borderRadius: '50%',
                                                                                '&:hover': {
                                                                                    backgroundColor: '#E5E5E5',
                                                                                },
                                                                            }}
                                                                        >
                                                                            <AddIcon />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                )}
                                                            </Box>
                                                        ),
                                                    }}
                                                />
                                            )}
                                        />

                                    </Box>
                                </Box>
                            </Grid>

                            {/* Order Field */}

                            {
                                !isTravelEnabled &&
                                <Grid item xs={12} sm={6}>
                                    <Autocomplete
                                        options={orders}
                                        getOptionLabel={(option) => option.order_number || ''}
                                        onChange={handleOrderChange}
                                        disabled={!selectedAccount}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Order"
                                                variant="outlined"
                                                margin="normal"
                                                fullWidth
                                                InputProps={{
                                                    ...params.InputProps,
                                                    endAdornment: (
                                                        <>
                                                            {loadingOrders && <CircularProgress color="inherit" size={20} />}
                                                            {params.InputProps.endAdornment}
                                                        </>
                                                    ),
                                                }}
                                                sx={{ height: '40px' }}
                                            />
                                        )}
                                    />
                                </Grid>
                            }

                            {/* Travel Order Field */}

                            {
                                isTravelEnabled &&
                                <Grid item xs={12} sm={6}>
                                    <Autocomplete
                                        options={travelOrders}
                                        getOptionLabel={(option) => option.name || ''}
                                        onChange={(_, newValue) => {
                                            setFormData({
                                                ...formData,
                                                travel_order_id: newValue?.id || null,
                                            });
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Travel Order"
                                                variant="outlined"
                                                margin="normal"
                                                fullWidth
                                                InputProps={{
                                                    ...params.InputProps,
                                                    endAdornment: (
                                                        <>
                                                            {loadingTravelOrders && <CircularProgress color="inherit" size={20} />}
                                                            {params.InputProps.endAdornment}
                                                        </>
                                                    ),
                                                }}
                                                sx={{ height: '40px' }}
                                            />
                                        )}
                                    />
                                </Grid>

                            }

                            {/* Assigned to Field */}

                            <Grid item xs={12} sm={6}>
                                <Autocomplete
                                    options={users}
                                    getOptionLabel={(option) => option.username} // Display username
                                    value={users.find((u) => u.id === formData.assigned_to) || null} // Pre-fill with current user
                                    onChange={(_, newValue) => {
                                        setFormData({
                                            ...formData,
                                            assigned_to: newValue?.id || '',
                                            assigned_to_name: newValue?.username || '',
                                        });
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Assigned To"
                                            variant="outlined"
                                            margin="normal"
                                            fullWidth
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <>
                                                        {/* {loadingAccounts ? <CircularProgress color="inherit" size={20} /> : null} */}
                                                        {params.InputProps.endAdornment} {/* Ensure dropdown icon is included */}
                                                    </>
                                                ),
                                            }}
                                            sx={{ height: '35px' }}
                                        />
                                    )}
                                />
                            </Grid>

                            {/* Priority Field */}

                            {
                                !isTravelEnabled &&
                                <Grid item xs={12} sm={6}>
                                    <Autocomplete
                                        options={["Low", "Medium", "High", "Urgent"]}
                                        getOptionLabel={(option) => option || ''}
                                        value={formData.priority}
                                        onChange={(_, newValue) => handleChange('priority', newValue)}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Priority"
                                                variant="outlined"
                                                margin="normal"
                                                fullWidth
                                                error={!!formErrors.priority}
                                                helperText={formErrors.priority}
                                                sx={{ height: '40px' }}
                                            />
                                        )}
                                    />
                                </Grid>
                            }

                            {/* Status Field */}

                            <Grid item xs={12} sm={6}>
                                <Autocomplete
                                    freeSolo
                                    options={statusOptions}
                                    getOptionLabel={(option) => option || ''}
                                    value={formData.status}
                                    onChange={(_, newValue) => handleChange('status', newValue)}
                                    onInputChange={(_, newInputValue, reason) => {
                                        if (reason === 'input') handleChange('status', newInputValue);
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            required
                                            label="Status"
                                            variant="outlined"
                                            margin="normal"
                                            fullWidth
                                            error={!!formErrors.status}
                                            helperText={formErrors.status}
                                            sx={{ height: '40px' }}
                                        />
                                    )}
                                />
                            </Grid>

                            {/* Internal Status Field */}

                            <Grid item xs={12} sm={6}>
                                <Autocomplete
                                    freeSolo
                                    options={internalStatusOptions}
                                    getOptionLabel={(option) => option || ''}
                                    value={formData.internal_status}
                                    onChange={(_, newValue) => handleChange('internal_status', newValue)}
                                    onInputChange={(_, newInputValue, reason) => {
                                        if (reason === 'input') handleChange('internal_status', newInputValue);
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Internal Status"
                                            variant="outlined"
                                            margin="normal"
                                            fullWidth
                                            error={!!formErrors.internal_status}
                                            helperText={formErrors.internal_status}
                                            sx={{ height: '40px' }}
                                        />
                                    )}
                                />
                            </Grid>

                            {/* Email Field */}

                            {
                                isTravelEnabled &&
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        name="email"
                                        label="Email"
                                        variant="outlined"
                                        margin="normal"
                                        fullWidth
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        sx={{ height: '50px' }}
                                        error={!!formErrors.email}
                                        helperText={formErrors.email}
                                    />
                                </Grid>
                            }

                            {/* Case Source Field */}

                            <Grid item xs={12} sm={6}>
                                <Autocomplete
                                    freeSolo
                                    options={caseSourceOptions}
                                    getOptionLabel={(option) => option || ''}
                                    value={formData.case_source}
                                    onChange={(_, newValue) => handleChange('case_source', newValue)}
                                    onInputChange={(_, newInputValue, reason) => {
                                        if (reason === 'input') handleChange('case_source', newInputValue);
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            required
                                            label="Case Source"
                                            variant="outlined"
                                            margin="normal"
                                            fullWidth
                                            error={!!formErrors.case_source}
                                            helperText={formErrors.case_source}
                                            sx={{ height: '40px' }}
                                        />
                                    )}
                                />
                            </Grid>

                            {/* Category Field */}

                            <Grid item xs={12} sm={6}>
                                <Autocomplete
                                    freeSolo
                                    options={categoryOptions}
                                    getOptionLabel={(option) => option || ''}
                                    value={formData.category}
                                    onChange={(_, newValue) => handleChange('category', newValue)}
                                    onInputChange={(_, newInputValue, reason) => {
                                        if (reason === 'input') handleChange('category', newInputValue);
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            required
                                            label="Category"
                                            variant="outlined"
                                            margin="normal"
                                            fullWidth
                                            error={!!formErrors.category}
                                            helperText={formErrors.category}
                                            sx={{ height: '40px' }}
                                        />
                                    )}
                                />
                            </Grid>

                            {/* Sub Category Field */}

                            <Grid item xs={12} sm={6}>
                                <Autocomplete
                                    freeSolo
                                    options={subCategoryOptions[formData.category]}
                                    getOptionLabel={(option) => option || ''}
                                    onChange={(_, newValue) => handleChange('sub_category', newValue)}
                                    onInputChange={(_, newInputValue, reason) => {
                                        if (reason === 'input') handleChange('sub_category', newInputValue);
                                    }}
                                    disabled={!formData?.category}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Sub Category"
                                            variant="outlined"
                                            margin="normal"
                                            fullWidth
                                            error={!!formErrors.sub_category}
                                            helperText={formErrors.sub_category}
                                            InputProps={{
                                                ...params.InputProps,
                                            }}
                                            sx={{ height: '40px' }}
                                        />
                                    )}
                                />
                            </Grid>

                            {/* Case Reason Field */}

                            {
                                !isTravelEnabled &&
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        name="case_reason"
                                        label="Case Reason"
                                        variant="outlined"
                                        margin="normal"
                                        fullWidth
                                        value={formData.case_reason}
                                        onChange={handleInputChange}
                                        sx={{ height: '50px' }}
                                    />
                                </Grid>
                            }

                            {/* Travel Order Pax - Disabled */}

                            {
                                (isTravelEnabled && !isTravelEnabled) &&
                                <Grid item xs={12} sm={6}>
                                    <Autocomplete
                                        options={orderPax}
                                        getOptionLabel={(option) => option.passenger_name || ''}
                                        disabled={!formData.travel_order_id || loadingOrderPax}
                                        onChange={(_, newValue) => {
                                            setFormData({
                                                ...formData,
                                                order_pax_id: newValue?.id || null,
                                            });
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Order Passenger"
                                                variant="outlined"
                                                margin="normal"
                                                fullWidth
                                                InputProps={{
                                                    ...params.InputProps,
                                                    endAdornment: (
                                                        <>
                                                            {loadingOrderPax && <CircularProgress color="inherit" size={20} />}
                                                            {params.InputProps.endAdornment}
                                                        </>
                                                    ),
                                                }}
                                                sx={{ height: '40px' }}
                                            />
                                        )}
                                    />
                                </Grid>
                            }

                            {/* Customer Field - Disabled */}

                            {
                                (isTravelEnabled && !isTravelEnabled) &&
                                <Grid item xs={12} sm={6}>
                                    <Autocomplete
                                        options={accounts}
                                        getOptionLabel={(option) => option.name || ''}
                                        onChange={(_, newValue) => {
                                            setFormData({
                                                ...formData,
                                                customer_id: newValue?.id || null,
                                            });
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Customer"
                                                variant="outlined"
                                                margin="normal"
                                                fullWidth
                                                InputProps={{
                                                    ...params.InputProps,
                                                    endAdornment: (
                                                        <>
                                                            {loadingAccounts && <CircularProgress color="inherit" size={20} />}
                                                            {params.InputProps.endAdornment}
                                                        </>
                                                    ),
                                                }}
                                                sx={{ height: '40px' }}
                                            />
                                        )}
                                    />
                                </Grid>
                            }

                            {/* Merchant Field */}

                            {
                                (isTravelEnabled && !isTravelEnabled) &&
                                <Grid item xs={12} sm={6}>
                                    <Autocomplete
                                        options={merchants}
                                        getOptionLabel={(option) => option.name || ''}
                                        disabled={loadingMerchants}
                                        onChange={(_, newValue) => {
                                            setFormData({
                                                ...formData,
                                                merchant_id: newValue?.id || null,
                                            });
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Merchant"
                                                variant="outlined"
                                                margin="normal"
                                                fullWidth
                                                InputProps={{
                                                    ...params.InputProps,
                                                    endAdornment: (
                                                        <>
                                                            {loadingMerchants && <CircularProgress color="inherit" size={20} />}
                                                            {params.InputProps.endAdornment}
                                                        </>
                                                    ),
                                                }}
                                                sx={{ height: '40px' }}
                                            />
                                        )}
                                    />
                                </Grid>
                            }

                            {/* Coupon Field */}

                            {
                                (isTravelEnabled && !isTravelEnabled) &&
                                <Grid item xs={12} sm={6}>
                                    <Autocomplete
                                        options={coupons}
                                        getOptionLabel={(option) => `${option.name} (${option.code})` || ''}
                                        disabled={loadingCoupons}
                                        onChange={(_, newValue) => {
                                            setFormData({
                                                ...formData,
                                                coupon_id: newValue?.id || null,
                                            });
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Coupon"
                                                variant="outlined"
                                                margin="normal"
                                                fullWidth
                                                InputProps={{
                                                    ...params.InputProps,
                                                    endAdornment: (
                                                        <>
                                                            {loadingCoupons && <CircularProgress color="inherit" size={20} />}
                                                            {params.InputProps.endAdornment}
                                                        </>
                                                    ),
                                                }}
                                                sx={{ height: '40px' }}
                                            />
                                        )}
                                    />
                                </Grid>
                            }

                            {/* Subject Field */}

                            <Grid item xs={12}>
                                <TextField
                                    required
                                    name="subject"
                                    label="Subject"
                                    variant="outlined"
                                    margin="normal"
                                    fullWidth
                                    value={formData.subject}
                                    onChange={handleInputChange}
                                    error={!!formErrors.subject}
                                    helperText={formErrors.subject}
                                    sx={{ height: '50px' }}
                                />
                            </Grid>

                            {/* Description Field */}

                            <Grid item xs={12}>
                                <TextField
                                    required
                                    name="description"
                                    label="Description"
                                    variant="outlined"
                                    margin="normal"
                                    fullWidth
                                    multiline
                                    rows={4}
                                    value={formData.description}
                                    onChange={handleInputChange}
                                    error={!!formErrors.description}
                                    helperText={formErrors.description}
                                />
                            </Grid>

                            {/* Buttons */}

                            <Grid item xs={12} >
                                <Box
                                    sx={{
                                        position: 'sticky',
                                        bottom: '-50px',
                                        bgcolor: 'background.paper',
                                        display: 'flex',
                                        justifyContent: 'right',
                                        padding: '16px',
                                        zIndex: 10,
                                    }}
                                >
                                    <MDButton
                                        onClick={onClose} // Correctly call the onClose function
                                        color="secondary"
                                        sx={{ mr: 2 }}
                                        disabled={isSaving}
                                    >
                                        Cancel
                                    </MDButton>
                                    <MDButton
                                        type="submit"
                                        variant="gradient"
                                        color="info"
                                        disabled={isSaving}
                                        sx={{
                                            minWidth: '100px',
                                            position: 'relative'
                                        }}
                                    >
                                        {isSaving ? (
                                            <CircularProgress
                                                size={24}
                                                color="inherit"
                                                sx={{
                                                    position: 'absolute',
                                                    top: '50%',
                                                    left: '50%',
                                                    marginTop: '-12px',
                                                    marginLeft: '-12px'
                                                }}
                                            />
                                        ) : 'Save'}
                                    </MDButton>
                                </Box>

                            </Grid>

                        </Grid>
                    </form>

                    <AddContactModal
                        isOpen={isContactModalOpen}
                        onClose={handleCloseContactModal}
                        onSave={handleNewContactSave}
                        accountId={selectedAccount?.id}
                        onContactAdded={fetchContacts}
                    />
                </Box>
            </Box>
        </Modal>
    );
    // return { handleSaveCase };
};

export default AddCaseModal;